import React, { useCallback } from 'react';

import { useTranslation } from 'react-i18next';

import { IconCheck } from '@pkg/assets/icons';
import { ChipColor, ChipSize } from '@pkg/components/Sections/Profile/Chip';
import { ChipMember } from '@pkg/components/Sections/Profile/Chips/ChipMember';
import { DepButton, DepButtonVariant } from '@pkg/components/deprecated';
import { useGetGroupInvites, useInviteMember } from '@pkg/hooks';

import { type ListUsersResponseUser } from '@app/client';

import './SearchResult.scss';

export interface SearchResultProps {
    groupId: string;
    resultIsMember: boolean;
    memberResult: ListUsersResponseUser;
}

export function SearchResult({ memberResult, groupId, resultIsMember }: SearchResultProps) {
    const { t: translate } = useTranslation(['groups']);

    const { data: groupInvites } = useGetGroupInvites({ groupId });
    const { mutate, isLoading, isSuccess } = useInviteMember();

    // Check if the user has been invited, then prevent showing "invite" for them.
    const isUserInvited = groupInvites?.find((inv) => inv.invitee.id === memberResult.id);
    const isInvited = isSuccess || isUserInvited;

    const handleInviteClick = useCallback(() => {
        if (groupId && memberResult.id) {
            mutate({
                groupId,
                memberIds: [memberResult.id],
            });
        }
    }, [groupId, memberResult.id, mutate]);

    // TODO: @shanimal Check members search result.
    // TODO: We should only use `twitter` or `twitter_handle` after BUG-660
    const twitter = (memberResult.twitter || memberResult.twitter_handle)?.replace(/[^a-zA-Z0-9_]+/g, '');

    return (
        <div className="yat-group-members__search-result">
            <ChipMember
                to={memberResult.primary_yat ? `/${memberResult.primary_yat}` : undefined}
                twitter={twitter}
                yat={memberResult.primary_yat || undefined}
                color={ChipColor.Transparent}
                size={ChipSize.Small}
            />

            {/*
                TODO: @shanimal Should we show the delete button here
                if they are a member (and user is owner)?
            */}
            {!resultIsMember && (
                <DepButton
                    size="x-small"
                    variant={isInvited ? DepButtonVariant.primary : DepButtonVariant.muted}
                    loading={isLoading}
                    interactive={!isInvited}
                    onClick={handleInviteClick}
                >
                    {isInvited && (
                        <span className="yat-group-members__search-result__icon">
                            <IconCheck />
                        </span>
                    )}

                    {isInvited ? translate('groups_invited') : translate('groups_invite')}
                </DepButton>
            )}
        </div>
    );
}
